import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KnownIssuesKnownIssue as KnownIssue, KnownIssuesKnownIssueCreate as KnownIssueCreate, KnownIssuesKnownIssueStatus as KnownIssueStatus, KnownIssuesKnownIssueUpdate as KnownIssueUpdate } from '../generated/api/types.gen.ts';
import { knownIssuesCreate, knownIssuesGet, knownIssuesList, knownIssuesUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { sakNoekkel } from './useSubmissions.ts';

export type { KnownIssue, KnownIssueCreate, KnownIssueStatus, KnownIssueUpdate };

export interface KjentFeilFilter {
  status?: KnownIssueStatus;
  productId?: string;
}

export const SIDESTØRRELSE = 100;

/** Nøklene eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). */
export const kjentFeilNoekkel = {
  alle: ['known-issues'] as const,
  liste: (filter: KjentFeilFilter) => ['known-issues', 'liste', filter] as const,
  detalj: (id: string) => ['known-issues', 'detalj', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Registeret (M2.4): aktive først, nyeste øverst — sortering og markør er serverens. */
export function useKjenteFeil(filter: KjentFeilFilter = {}) {
  const klient = useApiKlient();
  return useInfiniteQuery({
    queryKey: kjentFeilNoekkel.liste(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await knownIssuesList({ client: klient, query: { ...filter, cursor: pageParam, limit: SIDESTØRRELSE } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as { items: KnownIssue[]; nextCursor?: string };
    },
    getNextPageParam: (siste) => siste.nextCursor ?? undefined,
  });
}

/** Én kjent feil med tellere — hentes FØR Rettet, så dialogen viser hvor mange som varsles (kontrakten). */
export function useKjentFeil(id: string | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: kjentFeilNoekkel.detalj(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<KnownIssue> => {
      const res = await knownIssuesGet({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as KnownIssue;
    },
  });
}

/** Ny kjent feil — fra bunnen, eller fra en sak («Gjør til kjent feil»): da endres saken også, og sakslista er utdatert. */
export function useOpprettKjentFeil() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: KnownIssueCreate): Promise<KnownIssue> => {
      const res = await knownIssuesCreate({ client: klient, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as KnownIssue;
    },
    onSuccess: (feil, body) => {
      queryClient.setQueryData(kjentFeilNoekkel.detalj(feil.id), feil);
      void queryClient.invalidateQueries({ queryKey: kjentFeilNoekkel.alle });
      if (body.fromSubmissionId) void queryClient.invalidateQueries({ queryKey: sakNoekkel.alle });
    },
  });
}

/** Delvis oppdatering; Rettet (status = fikset) tar sakene bak til complete, så sakslista er utdatert etterpå. */
export function useOppdaterKjentFeil() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: KnownIssueUpdate }): Promise<KnownIssue> => {
      const res = await knownIssuesUpdate({ client: klient, path: { id }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as KnownIssue;
    },
    onSuccess: (feil, { body }) => {
      queryClient.setQueryData(kjentFeilNoekkel.detalj(feil.id), feil);
      void queryClient.invalidateQueries({ queryKey: kjentFeilNoekkel.alle });
      if (body.status === 'fikset') void queryClient.invalidateQueries({ queryKey: sakNoekkel.alle });
    },
  });
}
